/**
 * Light/dark for this reader, on this machine: the choice lives in localStorage (never on the
 * server) and is written onto <html data-theme>. The verdicts are pure in utils/theme.ts; this
 * file only does the storage and the DOM.
 *
 * 🚨 ONE store for the whole app, not one `useState` per caller (14/09 — docs/14-vessel-map
 * bước 3c). Until the map arrived, the top bar was the only reader of this hook, so a private
 * `useState` was enough. The map has to be handed a concrete style file, which means a SECOND
 * component now reads the theme — and two private states do not talk to each other: pressing
 * the toggle would repaint the page and leave the map in the old theme, with nothing failing.
 *
 * ⚠️ Reading or writing localStorage throws in a locked-down browser profile — both sides are
 * wrapped, and the page still paints in the default theme when storage is unavailable.
 * ⚠️ No browser gate covers this file (docs/00-project-rules/02 §6.3): dò tay khi sửa.
 */
import { useCallback, useSyncExternalStore } from 'react';

import {
  DEFAULT_THEME,
  nextTheme,
  type ResolvedTheme,
  resolveTheme,
  type Theme,
  THEME_STORAGE_KEY,
  themeAttribute,
  themeFromStorage,
} from '../utils/theme.js';

/** The stored choice of this reader — the default when storage is unreadable. */
export function readStoredTheme(): Theme {
  try {
    return themeFromStorage(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

/**
 * Write a theme onto <html> WITHOUT touching storage: a screen that forces a theme on itself
 * (the sign-in card) must never overwrite what the reader chose.
 */
export function applyTheme(theme: Theme): void {
  const attribute = themeAttribute(theme);
  if (attribute === null) document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', attribute);
}

let current: Theme | null = null;
const listeners = new Set<() => void>();

function readCurrent(): Theme {
  if (current === null) {
    current = readStoredTheme();
    applyTheme(current);
  }
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The one place a theme change happens: DOM, storage, then everyone who is reading. */
export function setTheme(theme: Theme): void {
  current = theme;
  applyTheme(theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* A browser that refuses storage still gets the theme it can see. */
  }
  for (const listener of [...listeners]) listener();
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, readCurrent, () => DEFAULT_THEME);
  const toggle = useCallback(() => {
    setTheme(nextTheme(readCurrent()));
  }, []);
  return { theme, setTheme, toggle };
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * The theme the map must be PAINTED in — `system` already resolved, and kept in step when the
 * OS switches while the tab is open. The map reads this, not `theme`: handing a WebGL style the
 * string 'system' paints a light map on a dark page and nothing errors.
 */
export function useResolvedTheme(): ResolvedTheme {
  const { theme } = useTheme();
  const prefersDark = useSyncExternalStore(
    (onChange) => {
      const query = window.matchMedia(DARK_QUERY);
      query.addEventListener('change', onChange);
      return () => {
        query.removeEventListener('change', onChange);
      };
    },
    () => window.matchMedia(DARK_QUERY).matches,
    () => true,
  );
  return resolveTheme(theme, prefersDark);
}
